<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">

    <!-- Sidebar Gauche -->
    <aside class="w-72 bg-white border-r border-slate-200 flex flex-col z-20 shadow-sm">
      <div class="p-6 border-b border-slate-100">
        <h1 class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Sélection Langue</h1>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Écran d'accueil</p>
        <div class="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <p class="text-[10px] text-blue-700 font-semibold">💡 Design partagé pour toutes les langues</p>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-6">

        <!-- Canvas Background Settings -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Fond du Canvas</h3>
          <div class="space-y-3 px-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Type de fond</label>
              <select v-model="canvasBackgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="color">Couleur unie</option>
                <option value="gradient">Dégradé</option>
                <option value="image">Image</option>
              </select>
            </div>

            <div v-if="canvasBackgroundType === 'color'">
              <label class="text-[10px] text-slate-500 mb-1 block">Couleur de fond</label>
              <input type="color" v-model="currentLanguageConfig.branding.colors.background" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div v-if="canvasBackgroundType === 'gradient'" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 1</label>
                <input type="color" v-model="gradientColor1" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 2</label>
                <input type="color" v-model="gradientColor2" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Direction</label>
                <select v-model="gradientDirection" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="to right">Horizontal →</option>
                  <option value="to bottom">Vertical ↓</option>
                  <option value="to bottom right">Diagonal ↘</option>
                </select>
              </div>
            </div>

            <div v-if="canvasBackgroundType === 'image'">
              <label class="text-[10px] text-slate-500 mb-1 block">URL de l'image</label>
              <MediaPicker
                v-model="canvasBackgroundImage"
                placeholder="URL de l'image de fond"
                button-text="Choisir l'image de fond"
                :agency-id="agencyId"
              />
              <label class="text-[10px] text-slate-500 mb-1 block mt-2">Ajustement</label>
              <select v-model="canvasBackgroundSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="cover">Couvrir</option>
                <option value="contain">Contenir</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Éléments sur la scène -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Éléments sur la scène</h3>
          <div class="space-y-1 px-2">
            <button
              v-for="(block, bIdx) in currentBlocks"
              :key="block.id"
              @click="selectBlock(bIdx)"
              :class="['w-full text-left px-3 py-2 rounded-lg text-xs transition-all',
                       selectedBlockIndex === bIdx ? 'bg-blue-500 text-white' : 'bg-slate-50 hover:bg-slate-100']"
            >
              <span class="mr-2">{{ getBlockIcon(block.type) }}</span>
              <span>{{ getBlockLabel(block) }}</span>
            </button>
          </div>

          <button @click="showAddBlockMenu = true" class="w-full mt-3 py-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-xs font-bold hover:border-blue-400 hover:text-blue-500 transition-all">
            + Ajouter un élément
          </button>
        </div>
      </div>

      <div class="p-4 border-t border-slate-100 space-y-2">
        <!-- Gestion des langues -->
        <button @click="showLanguageManagement = true" class="w-full py-2 bg-white border border-slate-200 rounded-lg hover:border-blue-400 text-xs font-semibold text-slate-700 hover:text-blue-600 transition-all">
          ⚙️ Gérer les langues disponibles
        </button>

        <!-- Import/Export Buttons -->
        <div class="flex gap-2">
          <button @click="exportConfig"
                  class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all">
            📥 Export
          </button>
          <label class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all cursor-pointer text-center">
            📤 Import
            <input type="file" accept=".json" @change="importConfig" class="hidden" />
          </label>
        </div>

        <button @click="saveConfig" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-2">
          <span>🚀 Publier</span>
        </button>
      </div>
    </aside>

    <!-- Canvas Preview -->
    <main class="flex-1 relative bg-slate-100 overflow-hidden p-12 flex justify-center items-center">
      <div class="relative bg-slate-800 p-4 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.4)] border-[8px] border-slate-700">
        <div
          id="canvas"
          class="relative overflow-hidden bg-white w-[375px] h-[667px] rounded-[2rem] shadow-inner"
          :style="getCanvasBackgroundStyle()"
          @mousemove="handleDrag"
          @mouseup="stopDrag"
        >
          <!-- Render blocks -->
          <div
            v-for="(block, bIdx) in currentBlocks"
            :key="block.id"
            :style="getBlockStyle(block)"
            @mousedown="startDrag($event, bIdx)"
            :class="['absolute cursor-move', selectedBlockIndex === bIdx ? 'ring-2 ring-blue-500' : '']"
          >
            <!-- Text Block -->
            <div v-if="block.type === 'text'"
                 :style="{
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   textAlign: block.textAlign,
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: block.textAlign === 'center' ? 'center' : (block.textAlign === 'right' ? 'flex-end' : 'flex-start'),
                   padding: '8px',
                   width: '100%',
                   height: '100%',
                   overflow: 'hidden'
                 }">
              {{ block.text }}
            </div>

            <!-- Image Block -->
            <img v-else-if="block.type === 'image'"
                 :src="block.icon || block.src"
                 :style="{ width: '100%', height: '100%', objectFit: block.fit || 'contain' }"
                 @error="$event.target.src = 'https://via.placeholder.com/400x120?text=Image+Error'"
                 alt="Block image" />

            <!-- Language Button Block -->
            <div v-else-if="block.type === 'language_button'"
                 :style="{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   gap: '8px',
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   width: '100%',
                   height: '100%'
                 }">
              <span :style="{ fontSize: '1.5em' }">{{ block.flag }}</span>
              <span>{{ block.label }}</span>
            </div>

            <!-- Button Block -->
            <div v-else-if="block.type === 'button'"
                 :style="{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   width: '100%',
                   height: '100%',
                   textAlign: 'center',
                   padding: '8px'
                 }">
              {{ block.text }}
            </div>

            <!-- Video Block -->
            <iframe v-else-if="block.type === 'video'"
                    :src="block.src || block.url"
                    style="width: 100%; height: 100%; border: none;" />

            <!-- Shape Blocks (rectangle, circle, triangle) -->
            <div v-else-if="block.type === 'triangle'"
                 :style="{
                   width: 0,
                   height: 0,
                   borderLeft: (block.width / SCALE_FACTOR / 2) + 'px solid transparent',
                   borderRight: (block.width / SCALE_FACTOR / 2) + 'px solid transparent',
                   borderBottom: (block.height / SCALE_FACTOR) + 'px solid ' + (block.backgroundColor || '#3B82F6')
                 }">
            </div>

            <!-- Rectangle and Circle (rendered via getBlockStyle) -->
            <div v-else style="width: 100%; height: 100%;"></div>
          </div>

          <!-- Alignment Guides -->
          <div v-for="(guide, idx) in alignmentGuides.vertical" :key="'v-'+idx"
               class="absolute top-0 bottom-0 w-[1px] pointer-events-none z-50"
               :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'"
               :style="{ left: (guide.value / SCALE_FACTOR) + 'px' }">
          </div>

          <div v-for="(guide, idx) in alignmentGuides.horizontal" :key="'h-'+idx"
               class="absolute left-0 right-0 h-[1px] pointer-events-none z-50"
               :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'"
               :style="{ top: (guide.value / SCALE_FACTOR) + 'px' }">
          </div>
        </div>
      </div>
    </main>

    <!-- Sidebar Droite - Property Panel -->
    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <PropertyPanel
        v-if="selectedBlock"
        :block="selectedBlock"
        :available-languages="config.enabledLanguages"
        @update="updateBlockProperty"
        @delete="deleteBlock"
        @clear-selection="clearSelection"
      >
        <template #image-picker="{ block }">
          <MediaPicker
            :model-value="block.icon || block.src"
            @update:model-value="updateBlockProperty({ key: 'icon', value: $event })"
            placeholder="URL de l'image"
            button-text="Choisir une image"
            :agency-id="agencyId"
          />
        </template>
      </PropertyPanel>

      <div v-else class="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-400">
        <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-2xl mb-4 grayscale">🎯</div>
        <p class="text-xs font-semibold">Cliquez sur un élément du canvas pour le régler.</p>
      </div>
    </aside>

    <!-- Block Type Menu Modal -->
    <BlockTypeMenu
      :show="showAddBlockMenu"
      @close="showAddBlockMenu = false"
      @select="addBlockToCanvas"
    />

    <!-- Language Management Modal -->
    <LanguageManagementModal
      :show="showLanguageManagement"
      :enabled-languages="config.enabledLanguages"
      @close="showLanguageManagement = false"
      @add-language="addLanguage"
      @remove-language="removeLanguage"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import { useAlignmentGuides } from '@/composables/useAlignmentGuides';
import { migrateToCanvasFormat, needsMigration, createEmptyV2Config } from '@/utils/migrateLanguageConfig';
import MediaPicker from '@/components/MediaPicker.vue';
import PropertyPanel from '@/components/PropertyPanel.vue';
import BlockTypeMenu from '@/components/BlockTypeMenu.vue';
import LanguageManagementModal from '@/components/LanguageManagementModal.vue';

// ----------------- CONFIGURATION DU CANVAS -----------------
const TARGET_WIDTH = 1080;
const EDITOR_WIDTH = 375;
const SCALE_FACTOR = TARGET_WIDTH / EDITOR_WIDTH;

const route = useRoute();
const agencyId = route.params.kioskId;

// États réactifs pour les langues
const activeLanguage = ref('fr'); // Toujours en français - design partagé pour toutes les langues
const showLanguageManagement = ref(false);
const showAddBlockMenu = ref(false);

// États pour le background du canvas
const canvasBackgroundType = ref('color');
const gradientColor1 = ref('#667eea');
const gradientColor2 = ref('#764ba2');
const gradientDirection = ref('to right');
const canvasBackgroundImage = ref('');
const canvasBackgroundSize = ref('cover');

const config = ref({
  agencyId: agencyId,
  version: 2.0,
  enabled: true,
  enabledLanguages: [
    { code: 'fr', label: 'Français', flag: '🇫🇷', order: 0, custom: false }
  ],
  defaultLanguage: 'fr',
  languages: {
    fr: {
      branding: {
        colors: { background: '#F8FAFC' },
        canvasBackground: { type: 'color' }
      },
      layout: { screenPadding: 24 },
      containers: [{
        type: 'language_selection_canvas',
        display: 'absolute',
        blocks: []
      }]
    }
  },
  autoSelectTimeout: {
    enabled: false,
    seconds: 10,
    defaultLanguage: 'fr'
  },
  targetResolution: {
    width: 1080,
    height: 1920
  }
});

// Configuration de la langue active


// 1. Définition de la structure par défaut (pour éviter la répétition)
const getDefaultLanguageConfig = () => ({
  branding: {
    colors: { background: '#F8FAFC' },
    canvasBackground: { type: 'color' }
  },
  layout: { screenPadding: 24 },
  containers: [{
    type: 'language_selection_canvas',
    display: 'absolute',
    blocks: []
  }]
});

// 2. Watcher pour gérer l'effet de bord (l'initialisation sécurisée)
// Note: On initialise seulement le français, toutes les autres langues partagent ce design
watch([activeLanguage, () => config.value.languages], () => {
  if (!config.value.languages) {
    config.value.languages = {};
  }

  // Initialiser uniquement le français si nécessaire
  if (!config.value.languages.fr) {
    config.value.languages.fr = getDefaultLanguageConfig();
  }
}, { immediate: true, deep: true });

// 3. Computed "propre" (lecture uniquement)
// Note: Retourne toujours le design du français, partagé par toutes les langues
const currentLanguageConfig = computed(() => {
  return config.value.languages?.fr || null;
});

// Blocs du canvas actif
const currentBlocks = computed(() => {
  return currentLanguageConfig.value?.containers?.[0]?.blocks || [];
});

// Drag & Drop State
const isDragging = ref(false);
const selectedBlockIndex = ref(null);
const startMousePos = { x: 0, y: 0 };
const startBlockPos = { x: 0, y: 0 };

// Alignment Guides
const { alignmentGuides, calculateAlignmentGuides, clearGuides } = useAlignmentGuides(1080, 1920, 20);

// Selected Block
const selectedBlock = computed(() => {
  if (selectedBlockIndex.value === null) return null;
  return currentBlocks.value[selectedBlockIndex.value];
});

// --- STYLE DU CANVAS ---
const getCanvasBackgroundStyle = () => {
  if (canvasBackgroundType.value === 'gradient') {
    return {
      background: `linear-gradient(${gradientDirection.value}, ${gradientColor1.value}, ${gradientColor2.value})`
    };
  } else if (canvasBackgroundType.value === 'image' && canvasBackgroundImage.value) {
    return {
      backgroundImage: `url(${canvasBackgroundImage.value})`,
      backgroundSize: canvasBackgroundSize.value,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  }
  return {
    backgroundColor: currentLanguageConfig.value?.branding?.colors?.background || '#F8FAFC'
  };
};

const getBlockStyle = (block) => {
  const baseStyle = {
    left: (block.x / SCALE_FACTOR) + 'px',
    top: (block.y / SCALE_FACTOR) + 'px',
    width: (block.width / SCALE_FACTOR) + 'px',
    height: (block.height / SCALE_FACTOR) + 'px',
    pointerEvents: 'auto'
  };

  // Add type-specific styles
  if (block.backgroundColor) {
    baseStyle.backgroundColor = block.backgroundColor;
  }
  if (block.borderRadius !== undefined) {
    baseStyle.borderRadius = (block.borderRadius / SCALE_FACTOR) + 'px';
  }
  if (block.borderWidth !== undefined && block.borderColor) {
    baseStyle.border = `${block.borderWidth / SCALE_FACTOR}px solid ${block.borderColor}`;
  }
  if (block.opacity !== undefined) {
    baseStyle.opacity = block.opacity;
  }

  return baseStyle;
};

const getBlockIcon = (type) => {
  const icons = {
    text: '✍️',
    image: '🖼️',
    language_button: '🌐',
    button: '🔘',
    rectangle: '▭',
    circle: '●',
    triangle: '▲',
    video: '🎬'
  };
  return icons[type] || '📦';
};

const getBlockLabel = (block) => {
  if (block.type === 'text') return block.text || 'Texte';
  if (block.type === 'language_button') return `Langue: ${block.label || block.languageCode}`;
  if (block.type === 'button') return block.text || 'Bouton';
  if (block.type === 'image') return 'Image';
  return block.type;
};

// --- DRAG & DROP ---
const startDrag = (e, bIdx) => {
  selectedBlockIndex.value = bIdx;
  isDragging.value = true;

  const block = currentBlocks.value[bIdx];
  startMousePos.x = e.clientX;
  startMousePos.y = e.clientY;
  startBlockPos.x = block.x || 0;
  startBlockPos.y = block.y || 0;
};

const handleDrag = (e) => {
  if (!isDragging.value || selectedBlockIndex.value === null) return;

  const block = currentBlocks.value[selectedBlockIndex.value];
  const dx = e.clientX - startMousePos.x;
  const dy = e.clientY - startMousePos.y;

  const newX = startBlockPos.x + (dx * SCALE_FACTOR);
  const newY = startBlockPos.y + (dy * SCALE_FACTOR);

  const guides = calculateAlignmentGuides(
    currentBlocks.value,
    selectedBlockIndex.value,
    newX,
    newY,
    block.width,
    block.height
  );

  const snapTolerance = 10;
  let snappedX = newX;
  let snappedY = newY;

  if (guides.vertical.length > 0) {
    const closestV = guides.vertical[0];
    if (Math.abs(closestV.value - (newX + block.width / 2)) < snapTolerance) {
      snappedX = closestV.value - block.width / 2;
    } else if (Math.abs(closestV.value - newX) < snapTolerance) {
      snappedX = closestV.value;
    } else if (Math.abs(closestV.value - (newX + block.width)) < snapTolerance) {
      snappedX = closestV.value - block.width;
    }
  }

  if (guides.horizontal.length > 0) {
    const closestH = guides.horizontal[0];
    if (Math.abs(closestH.value - (newY + block.height / 2)) < snapTolerance) {
      snappedY = closestH.value - block.height / 2;
    } else if (Math.abs(closestH.value - newY) < snapTolerance) {
      snappedY = closestH.value;
    } else if (Math.abs(closestH.value - (newY + block.height)) < snapTolerance) {
      snappedY = closestH.value - block.height;
    }
  }

  block.x = snappedX;
  block.y = snappedY;
  alignmentGuides.value = guides;
};

const stopDrag = () => {
  isDragging.value = false;
  clearGuides();
};

// --- BLOCK MANAGEMENT ---
const selectBlock = (bIdx) => {
  selectedBlockIndex.value = bIdx;
};

const clearSelection = () => {
  selectedBlockIndex.value = null;
};

const addBlockToCanvas = (type) => {
  const newBlock = {
    id: `${type}-${Date.now()}`,
    type: type,
    x: 540,
    y: 960,
    width: 400,
    height: 120,
    ...getDefaultPropsForType(type)
  };

  currentBlocks.value.push(newBlock);
  showAddBlockMenu.value = false;
  toast.success('Élément ajouté');
};

const getDefaultPropsForType = (type) => {
  switch (type) {
    case 'text':
      return {
        text: 'Nouveau texte',
        fontSize: 32,
        fontWeight: '600',
        fontFamily: 'Plus Jakarta Sans',
        color: '#1E293B',
        textAlign: 'center'
      };
    case 'image':
      return {
        src: 'https://via.placeholder.com/400x120?text=Image',
        icon: 'https://via.placeholder.com/400x120?text=Image',
        fit: 'contain',
        opacity: 1
      };
    case 'language_button':
      return {
        languageCode: config.value.enabledLanguages[0]?.code || 'fr',
        label: config.value.enabledLanguages[0]?.label || 'Français',
        flag: config.value.enabledLanguages[0]?.flag || '🇫🇷',
        backgroundColor: '#FFFFFF',
        color: '#1E293B',
        borderRadius: 20,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        fontSize: 28,
        fontWeight: '600',
        fontFamily: 'Plus Jakarta Sans',
        textAlign: 'center',
        action: { type: 'language', queueId: null, url: '' }
      };
    case 'button':
      return {
        text: 'Nouveau bouton',
        fontSize: 28,
        fontWeight: '600',
        fontFamily: 'Plus Jakarta Sans',
        color: '#FFFFFF',
        backgroundColor: '#3B82F6',
        textAlign: 'center',
        borderRadius: 12,
        action: { type: 'none', queueId: null, url: '' }
      };
    case 'rectangle':
      return {
        backgroundColor: '#3B82F6',
        borderRadius: 12,
        borderWidth: 0,
        opacity: 1
      };
    case 'circle':
      return {
        backgroundColor: '#3B82F6',
        borderRadius: 200,
        borderWidth: 0,
        opacity: 1,
        width: 200,
        height: 200
      };
    case 'triangle':
      return {
        backgroundColor: '#3B82F6',
        borderWidth: 0,
        opacity: 1
      };
    case 'video':
      return {
        src: '',
        url: ''
      };
    default:
      return {};
  }
};

const updateBlockProperty = ({ key, value }) => {
  if (selectedBlock.value && selectedBlockIndex.value !== null) {
    currentBlocks.value[selectedBlockIndex.value][key] = value;

    // Si on met à jour 'icon', mettre à jour aussi 'src' pour les images
    if (key === 'icon' && currentBlocks.value[selectedBlockIndex.value].type === 'image') {
      currentBlocks.value[selectedBlockIndex.value].src = value;
    }
  }
};

const deleteBlock = () => {
  if (selectedBlockIndex.value !== null) {
    currentBlocks.value.splice(selectedBlockIndex.value, 1);
    selectedBlockIndex.value = null;
    toast.success('Élément supprimé');
  }
};

// --- LANGUAGE MANAGEMENT ---
const addLanguage = (lang) => {
  config.value.enabledLanguages.push(lang);

  // Note: On ne crée PAS de design séparé pour chaque langue
  // Toutes les langues partagent le design de 'fr'

  toast.success(`${lang.label} ajouté`);
};

const removeLanguage = (idx) => {
  const lang = config.value.enabledLanguages[idx];

  // Empêcher la suppression du français (design principal)
  if (lang.code === 'fr') {
    toast.error("Impossible de supprimer le français (langue du design)");
    return;
  }

  config.value.enabledLanguages.splice(idx, 1);

  // Note: On ne supprime PAS le design car toutes les langues partagent le design de 'fr'

  toast.success(`${lang.label} supprimé`);
};

// --- LOAD/SAVE CONFIG ---
const fetchConfig = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/language-selection-config/${agencyId}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });

    if (res.status === 404) {
      // No config, use default v2.0
      config.value = createEmptyV2Config(agencyId);
      return;
    }

    let data = await res.json();

    // Check if migration is needed
    if (needsMigration(data)) {
      const shouldMigrate = confirm(
        "Une nouvelle version de l'éditeur est disponible avec drag & drop.\n" +
        "Voulez-vous migrer votre configuration?\n\n" +
        "Note: L'ancienne configuration sera préservée."
      );

      if (shouldMigrate) {
        data = migrateToCanvasFormat(data);
        toast.info("Configuration migrée vers le nouvel éditeur!");
      } else {
        toast.warning("Utilisation de l'ancien format. Certaines fonctionnalités ne sont pas disponibles.");
      }
    }

    config.value = data;

    // Load canvas background settings for active language
    loadCanvasBackgroundSettings();
  } catch (err) {
    toast.error("Erreur chargement config : " + err.message);
  }
};

const loadCanvasBackgroundSettings = () => {
  const bg = currentLanguageConfig.value?.branding?.canvasBackground;
  if (bg) {
    canvasBackgroundType.value = bg.type || 'color';
    if (bg.gradient) {
      gradientColor1.value = bg.gradient.color1;
      gradientColor2.value = bg.gradient.color2;
      gradientDirection.value = bg.gradient.direction;
    }
    if (bg.image) {
      canvasBackgroundImage.value = bg.image.url;
      canvasBackgroundSize.value = bg.image.size;
    }
  }
};

const saveConfig = async () => {
  // Save current canvas background settings
  const canvasBackground = {
    type: canvasBackgroundType.value
  };

  if (canvasBackgroundType.value === 'gradient') {
    canvasBackground.gradient = {
      color1: gradientColor1.value,
      color2: gradientColor2.value,
      direction: gradientDirection.value
    };
  } else if (canvasBackgroundType.value === 'image') {
    canvasBackground.image = {
      url: canvasBackgroundImage.value,
      size: canvasBackgroundSize.value
    };
  }

  currentLanguageConfig.value.branding.canvasBackground = canvasBackground;

  const idToast = toast.loading("Enregistrement...");
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/language-selection-config/${agencyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config.value)
    });
    if (!res.ok) throw new Error("Erreur sauvegarde");
    toast.update(idToast, { render: "🚀 Configuration publiée !", type: "success", isLoading: false, autoClose: 3000 });
  } catch (err) {
    toast.update(idToast, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
  }
};

// === EXPORT/IMPORT ===
const exportConfig = () => {
  // Save current canvas background before export
  const canvasBackground = {
    type: canvasBackgroundType.value
  };

  if (canvasBackgroundType.value === 'gradient') {
    canvasBackground.gradient = {
      color1: gradientColor1.value,
      color2: gradientColor2.value,
      direction: gradientDirection.value
    };
  } else if (canvasBackgroundType.value === 'image') {
    canvasBackground.image = {
      url: canvasBackgroundImage.value,
      size: canvasBackgroundSize.value
    };
  }

  currentLanguageConfig.value.branding.canvasBackground = canvasBackground;

  const dataStr = JSON.stringify(config.value, null, 2);
  const blob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `language-config-${agencyId}.json`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success("📥 Configuration exportée");
};

const importConfig = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    const text = await file.text();
    const importedConfig = JSON.parse(text);

    // Validate the imported config
    if (!importedConfig.enabledLanguages || !importedConfig.agencyId) {
      throw new Error("Format de configuration invalide");
    }

    // Update agencyId to current one
    importedConfig.agencyId = agencyId;

    // Check if migration is needed
    if (needsMigration(importedConfig)) {
      const shouldMigrate = confirm(
        "La configuration importée est dans l'ancien format.\n" +
        "Voulez-vous la migrer vers le nouveau format?"
      );

      if (shouldMigrate) {
        config.value = migrateToCanvasFormat(importedConfig);
        toast.success("📤 Configuration importée et migrée");
      } else {
        config.value = importedConfig;
        toast.success("📤 Configuration importée");
      }
    } else {
      config.value = importedConfig;
      toast.success("📤 Configuration importée");
    }

    // Reload canvas background settings
    loadCanvasBackgroundSettings();

    // Reset file input
    e.target.value = '';
  } catch (err) {
    toast.error("Erreur import : " + err.message);
  }
};

onMounted(() => {
  fetchConfig();
});
</script>

<style scoped>
/* Add any additional styles here */
</style>
